import { useEffect, useState, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFirebaseDb } from '../services/firebase';
import { updatePreferences, updateProfile, updateFeatureFlags } from '../services/user';
import type { UserFeatureFlags, UserProfile, UserPreferences } from '../types';

interface UseProfileReturn {
  profile: UserProfile | null;
  loading: boolean;
  updatePrefs: (prefs: Partial<UserPreferences>) => Promise<void>;
  updateProfileFields: (fields: Partial<Pick<UserProfile, 'preferredName'>>) => Promise<void>;
  updateFeatures: (features: Partial<UserFeatureFlags>) => Promise<void>;
}

export function useProfile(userId: string | null): UseProfileReturn {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const db = getFirebaseDb();
    const unsub = onSnapshot(
      doc(db, 'users', userId),
      snap => {
        if (snap.exists()) {
          setProfile({ userId: snap.id, ...snap.data() } as UserProfile);
        } else {
          setProfile(null);
        }
        setLoading(false);
      },
      () => setLoading(false)
    );

    return unsub;
  }, [userId]);

  const updatePrefs = useCallback(
    async (prefs: Partial<UserPreferences>) => {
      if (!userId) return;
      await updatePreferences(userId, prefs);
    },
    [userId]
  );

  const updateProfileFields = useCallback(
    async (fields: Partial<Pick<UserProfile, 'preferredName'>>) => {
      if (!userId) return;
      await updateProfile(userId, fields);
    },
    [userId]
  );

  const updateFeatures = useCallback(
    async (features: Partial<UserFeatureFlags>) => {
      if (!userId) return;
      await updateFeatureFlags(userId, features);
    },
    [userId]
  );

  return { profile, loading, updatePrefs, updateProfileFields, updateFeatures };
}
